/**
 * Papelera — soft-deleted expedientes, recoverable via "Restaurar". Anyone with
 * expediente.list may view the trash; only compliance_manager / tenant_admin may
 * restore (the server enforces this — the button is hidden for others as UX).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PapeleraRow } from '../lib/api';
import { formatDate, invalidateExpedienteTimeline, useMe } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { Badge, Button, Card, Dialog, EmptyState, PageHeader, Pill, Skeleton } from '../components/ui';
import { PersonaIcon } from '../components/icons';
import { riesgoTone } from '../components/compliance';
import { RIESGO_LABEL, type NivelRiesgo } from '../lib/phase1';
import { Trash2, RotateCcw } from 'lucide-react';

export default function Papelera() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  const canRestore = ['compliance_manager', 'tenant_admin'].some((r) => (me?.roles ?? []).includes(r));

  const { data, isLoading, isError } = useQuery({ queryKey: ['papelera'], queryFn: api.listPapelera });

  const [confirming, setConfirming] = useState<PapeleraRow | null>(null);
  const [restoring, setRestoring] = useState(false);

  async function onRestore() {
    if (!confirming) return;
    setRestoring(true);
    try {
      await api.restoreExpediente(confirming.id);
      await qc.invalidateQueries({ queryKey: ['papelera'] });
      await qc.invalidateQueries({ queryKey: ['expedientes'] });
      await invalidateExpedienteTimeline(qc, confirming.id);
      toast({ message: `Expediente de ${confirming.nombre} restaurado`, kind: 'success' });
      setConfirming(null);
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'No se pudo restaurar el expediente',
        kind: 'error',
      });
    } finally {
      setRestoring(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Papelera"
        subtitle="Expedientes eliminados. Se pueden restaurar mientras sigan aquí."
      />

      {isLoading ? (
        <div className="grid gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : isError ? (
        <Card>
          <p className="text-sm text-tone-red">No se pudo cargar la papelera.</p>
        </Card>
      ) : !data || data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Trash2 className="h-8 w-8" strokeWidth={1.5} />}
            title="La papelera está vacía"
            hint="Los expedientes que elimines aparecerán aquí y podrás restaurarlos."
          />
        </Card>
      ) : (
        <div className="grid gap-3">
          {data.map((row) => (
            <Card key={row.id} className="flex flex-wrap items-center gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary/10">
                <PersonaIcon persona={row.persona} className="h-6 w-6 text-primary-dark" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-ink">{row.nombre}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
                  <span className="font-mono">{row.docId}</span>
                  <Badge muted>{row.estado}</Badge>
                  {row.riesgo !== 'sin' && (
                    <Pill tone={riesgoTone(row.riesgo as NivelRiesgo)}>
                      Riesgo {RIESGO_LABEL[row.riesgo as NivelRiesgo]}
                    </Pill>
                  )}
                </div>
                <div className="mt-1 text-xs text-ink-faint">
                  Eliminado: {formatDate(row.deletedAt ?? row.updatedAt, 'sin fecha')}
                </div>
              </div>
              {canRestore && (
                <Button variant="outline" className="shrink-0" onClick={() => setConfirming(row)}>
                  <RotateCcw className="h-4 w-4" strokeWidth={2} /> Restaurar
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={!!confirming}
        title="Restaurar expediente"
        onClose={() => (restoring ? undefined : setConfirming(null))}
      >
        {confirming && (
          <>
            <p className="text-sm text-ink-soft">
              El expediente de <b className="text-ink">{confirming.nombre}</b> volverá a la lista de
              expedientes activos.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirming(null)} disabled={restoring}>
                Cancelar
              </Button>
              <Button onClick={() => void onRestore()} disabled={restoring}>
                {restoring ? 'Restaurando…' : 'Restaurar'}
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}
